import {
	applyFilterWithManager,
	FilterManager,
	KlevuFetch,
	KlevuFilterType,
	KlevuSearchSorting,
	listFilters,
	search,
	searchCms,
	sendSearchEvent,
	suggestions,
	trendingProducts,
	type KlevuFilterResultOptions,
	type KlevuFilterResultRating,
	type KlevuFilterResultSlider,
	type KlevuRecord,
} from "@klevu/core";
import is from "@sindresorhus/is";
import {
	FilterType,
	type Filter,
	type OptionsProps,
	type SearchResultCMS,
	type SearchResultProduct,
} from "../../hooks/search/types";

export type PartialFilter = Pick<Filter, "key"> & {
	options: Array<Pick<Filter["options"][0], "value" | "selected">>;
};

const colors: Record<string, string> = {
	beige: "#ebe1cf",
	black: "#222222",
	blue: "#6c849e",
	green: "#7c8067",
	gray: "#848484",
	silver: "#848484",
	pink: "#e3c4c1",
	red: "#a33f3f",
	purple: "#917c83",
	turquoise: "#ffffff",
	white: "#ffffff",
	yellow: "#e7ca88",
};

const manager = new FilterManager();

const preDefinedFilters: Filter[] = [
	{
		key: "sortby",
		label: "Sort by",
		translatedLabel: "",
		options: [],
		type: FilterType.sort,
	},
];

// TODO in this file: Refactor to adheres to DRY principle
const transformRecords = (records: KlevuRecord[]): SearchResultProduct[] => {
	return records
		.filter(
			(record: KlevuRecord) => is.nonEmptyString(record.image) && record.price,
		)
		.map(
			(record: KlevuRecord): SearchResultProduct => ({
				id: record.id,
				image: record.image,
				url: record.url,
				currency: record.currency,
				price: record.price,
				name: record.name,
				sku: record.sku,
				color: record.color as string, // TODO Add type guard
				imageHover: record.imageHover,
				fabric: record.fabric as string, // TODO Add type guard
			}),
		);
};

const transformFilters = (
	klevuFilters:
		| (
				| KlevuFilterResultOptions
				| KlevuFilterResultSlider
				| KlevuFilterResultRating
		  )[]
		| undefined,
): Filter[] => {
	if (klevuFilters === undefined || klevuFilters.length === 0) {
		return [];
	}

	const resultFilters = klevuFilters
		.filter((record) => record.key !== "color")
		.map(
			(record): Filter => ({
				key: record.key,
				label: record.key === "default_color" ? "Color" : record.label,
				translatedLabel: "",
				type: FilterType.dynamic,
				options:
					record.type === KlevuFilterType.Options
						? record.options
								.filter((option) => option.name !== "")
								.sort((a, b) => {
									return a.name.localeCompare(b.name);
								})
								.map((option) => {
									return record.key === "default_color"
										? ({
												name: option.name.split("::")[1] as string,
												value: option.value,
												hexOne: colors[option.name.split("::")[0] as string],
												multiColored: option.name.split("::")[0] === "multi",
											} as OptionsProps)
										: ({
												name: option.name,
												value: option.value,
											} as OptionsProps);
								})
						: [],
			}),
		);

	return [...preDefinedFilters, ...resultFilters];
};

function applySelectedFilterOptions(searchFilter: PartialFilter[] | undefined) {
	if (searchFilter) {
		for (const filter of searchFilter)
			for (const option of filter.options)
				if (option.value)
					if (option.selected) manager.selectOption(filter.key, option.value);
					else manager.deselectOption(filter.key, option.value);
	} else {
		manager.clear();
	}
}

export const applySearch = async (
	searchTerm: string,
	searchFilter: PartialFilter[] | undefined,
	page: number = 1,
	limit: number,
	searchSorting?: KlevuSearchSorting,
): Promise<{
	results: SearchResultProduct[];
	totalResultsFound: number;
	hasNextPage: boolean;
	suggestions: { suggest: string }[];
	filters: Filter[];
}> => {
	try {
		const offset = (page - 1) * limit;
		applySelectedFilterOptions(searchFilter);
		const result = await KlevuFetch(
			search(
				searchTerm,
				{
					limit,
					offset,
					sort: searchSorting ?? KlevuSearchSorting.Relevance,
				},
				listFilters({
					filterManager: manager,
				}),
				applyFilterWithManager(manager),
				sendSearchEvent(),
			),
			suggestions(searchTerm, {
				limit: 3, // Hardcode the limit since we only use it in the quickSearch
			}),
		);
		const query = result.queriesById("search");
		const records = query.records;
		const filters = query.filters?.sort((a, b) => {
			return a.key.localeCompare(b.key);
		});

		if (records.length === 0) {
			return {
				results: [],
				totalResultsFound: 0,
				hasNextPage: false,
				filters: [],
				suggestions: [],
			};
		}

		const suggestionResults = result.apiResponse.suggestionResults;
		const removeBoldTag = (str: string) => str.replaceAll(/<\/?b>/g, "");

		const cleanedSuggestions = (suggestionResults || []).map(
			(suggestionResult) => {
				return {
					...suggestionResult,
					suggestions: suggestionResult.suggestions.map((suggestion) => ({
						...suggestion,
						suggest: removeBoldTag(suggestion.suggest),
					})),
				};
			},
		);

		return {
			results: transformRecords(records),
			totalResultsFound: query.meta.totalResultsFound,
			hasNextPage: query.hasNextPage(),
			filters: transformFilters(filters),
			suggestions: cleanedSuggestions.flatMap((sr) => sr.suggestions),
		};
	} catch (error) {
		console.error("Error fetching search results:", error);
		return {
			results: [],
			totalResultsFound: 0,
			hasNextPage: false,
			filters: [],
			suggestions: [],
		};
	}
};

export const applyCMSSearch = async (
	searchTerm: string,
	page: number = 1,
	limit: number,
): Promise<{
	results: SearchResultCMS[];
	hasNextPage: boolean;
}> => {
	const cmsId = "searchCms";
	try {
		const offset = (page - 1) * limit;
		const result = await KlevuFetch(
			searchCms(searchTerm, {
				id: cmsId,
				limit,
				offset,
			}),
		);
		const cmsQuery = result.queriesById(cmsId);
		const records = cmsQuery.records;
		if (records.length === 0) {
			return { results: [], hasNextPage: false };
		}
		return {
			results: records,
			hasNextPage: limit * page < cmsQuery.meta.totalResultsFound,
		};
	} catch (error) {
		console.error("Error fetching cms results:", error);
		return { results: [], hasNextPage: false };
	}
};

export const applyTrendingProducts = async (
	searchFilter: PartialFilter[] | undefined,
	page: number = 1,
	limit: number = 4,
	searchSorting?: KlevuSearchSorting,
): Promise<{
	results: SearchResultProduct[];
	filters: Filter[];
	totalResultsFound: number;
	hasNextPage: boolean;
	suggestions: { suggest: string }[];
	limit: number;
}> => {
	try {
		const offset = (page - 1) * limit;
		applySelectedFilterOptions(searchFilter);
		const result = await KlevuFetch(
			trendingProducts(
				{
					limit,
					offset,
					sort: searchSorting ?? KlevuSearchSorting.Relevance,
				},
				listFilters({
					filterManager: manager,
				}),
				applyFilterWithManager(manager),
			),
		);

		const query = result.queriesById("trendingProducts");
		const records = query.records;
		const filters = query.filters?.sort((a, b) => {
			return a.key.localeCompare(b.key);
		});

		if (records.length === 0) {
			return {
				results: [],
				limit: limit,
				totalResultsFound: 0,
				hasNextPage: false,
				suggestions: [],
				filters: [],
			};
		}

		return {
			results: transformRecords(records),
			limit: limit,
			totalResultsFound: query.meta.totalResultsFound,
			hasNextPage: query.hasNextPage(),
			suggestions: [],
			filters: transformFilters(filters),
		};
	} catch (error) {
		console.error("Error fetching search suggestions:", error);
		return {
			results: [],
			limit: limit,
			totalResultsFound: 0,
			hasNextPage: false,
			suggestions: [],
			filters: [],
		};
	}
};
